export const DONUT_CX = 100;
export const DONUT_CY = 100;
export const DONUT_INNER_R = 62;
export const DONUT_OUTER_R = 92;
export const DONUT_COLOR_COUNT = 8;

/**
 * Convert a polar coordinate to SVG cartesian space. Angles are in degrees,
 * measured clockwise from 12 o'clock, so the first slice starts at the top.
 * @param {number} r radius from the donut centre
 * @param {number} angleDeg angle in degrees
 * @returns {{ x: number, y: number }}
 */
export function polarToCartesian(r, angleDeg) {
	const rad = ((angleDeg - 90) * Math.PI) / 180;
	return {
		x: DONUT_CX + r * Math.cos(rad),
		y: DONUT_CY + r * Math.sin(rad),
	};
}

/**
 * SVG path `d` string for a donut slice spanning startAngle -> endAngle
 * (degrees, clockwise). Drawn with an outer arc, inner arc, and straight
 * sides, so adjacent slices leave a clean gap at the seam.
 * @param {number} startAngle degrees
 * @param {number} endAngle degrees
 * @returns {string}
 */
export function donutSlicePath(startAngle, endAngle) {
	const sweep = endAngle - startAngle;
	// 360° is a full ring — a single arc would degenerate to a point, so
	// draw as two half-circles for outer and inner.
	if (sweep >= 359.999) {
		const outerTop = polarToCartesian(DONUT_OUTER_R, 0);
		const outerBottom = polarToCartesian(DONUT_OUTER_R, 180);
		const innerTop = polarToCartesian(DONUT_INNER_R, 0);
		const innerBottom = polarToCartesian(DONUT_INNER_R, 180);
		return [
			`M ${outerTop.x.toFixed(2)} ${outerTop.y.toFixed(2)}`,
			`A ${DONUT_OUTER_R} ${DONUT_OUTER_R} 0 1 1 ${outerBottom.x.toFixed(2)} ${outerBottom.y.toFixed(2)}`,
			`A ${DONUT_OUTER_R} ${DONUT_OUTER_R} 0 1 1 ${outerTop.x.toFixed(2)} ${outerTop.y.toFixed(2)}`,
			`L ${innerTop.x.toFixed(2)} ${innerTop.y.toFixed(2)}`,
			`A ${DONUT_INNER_R} ${DONUT_INNER_R} 0 1 0 ${innerBottom.x.toFixed(2)} ${innerBottom.y.toFixed(2)}`,
			`A ${DONUT_INNER_R} ${DONUT_INNER_R} 0 1 0 ${innerTop.x.toFixed(2)} ${innerTop.y.toFixed(2)}`,
			'Z',
		].join(' ');
	}
	const largeArc = sweep > 180 ? 1 : 0;
	const outerStart = polarToCartesian(DONUT_OUTER_R, startAngle);
	const outerEnd = polarToCartesian(DONUT_OUTER_R, endAngle);
	const innerStart = polarToCartesian(DONUT_INNER_R, startAngle);
	const innerEnd = polarToCartesian(DONUT_INNER_R, endAngle);
	return [
		`M ${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)}`,
		`A ${DONUT_OUTER_R} ${DONUT_OUTER_R} 0 ${largeArc} 1 ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)}`,
		`L ${innerEnd.x.toFixed(2)} ${innerEnd.y.toFixed(2)}`,
		`A ${DONUT_INNER_R} ${DONUT_INNER_R} 0 ${largeArc} 0 ${innerStart.x.toFixed(2)} ${innerStart.y.toFixed(2)}`,
		'Z',
	].join(' ');
}

/**
 * Compute donut slice geometry from a set of labelled values. Slices are
 * drawn clockwise from 12 o'clock, largest first. Values <= 0 are excluded
 * (a negative amount cannot be drawn as a positive arc); the caller keeps
 * them in the table so nothing is hidden.
 * @param {Record<string, number>} values
 * @returns {{ entries: Array<{ key: string, value: number, colorIndex: number, d: string }>, total: number }}
 */
export function computeDonutSlices(values) {
	const positive = Object.entries(values)
		.filter(([, v]) => v > 0)
		.sort((a, b) => b[1] - a[1]);
	const total = positive.reduce((sum, [, v]) => sum + v, 0);
	let angle = 0;
	const entries = positive.map(([key, value], i) => {
		const sweep = total > 0 ? (value / total) * 360 : 0;
		const d = donutSlicePath(angle, angle + sweep);
		angle += sweep;
		return { key, value, colorIndex: i % DONUT_COLOR_COUNT, d };
	});
	return { entries, total };
}
